import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { FaIdCard, FaBuilding, FaTimesCircle, FaTrashAlt } from "react-icons/fa";
import Sidebar from "../Sidebar";
import TopBar from "../TopBar";
import axios from "axios";
import { jwtDecode } from "jwt-decode";

/* ===== API base ===== */
const API_BASE =
  (process.env.REACT_APP_API_BASE && process.env.REACT_APP_API_BASE.trim()) ||
  "https://server229256.nazwa.pl";

/* ===== styled components (aligned with your dashboard pages) ===== */
const DashboardWrapper = styled.div`
  display: flex;
  height: 100vh;
  overflow: hidden;
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  background-color: #f5f5f5;
`;

const PageBody = styled.div`
  flex: 1;
  overflow: auto;
  padding: 20px;
  background-color: #f5f5f5;
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 20px 0 20px;
  font-size: 1.2rem;
  font-weight: bold;
  color: #87ceeb;
  margin-bottom: 16px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 20px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 10px rgba(0,0,0,0.06);
  border-radius: 12px;
  padding: 22px;
`;

const SectionHeader = styled.h3`
  margin: 0 0 14px 0;
  font-size: 1.05rem;
  color: #111827;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 16px;
  padding: 10px 0;
  border-top: 1px dashed #e5e7eb;

  &:first-child {
    border-top: none;
    padding-top: 0;
  }
`;

const Key = styled.div`
  color: #6b7280;
  font-weight: 600;
`;

const Val = styled.div`
  color: #111827;
  word-break: break-word;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 700;
  ${({ type }) => {
    if (type === "active")
      return `background: #dcfce7; color: #166534; border: 1px solid #bbf7d0;`;
    if (type === "demo")
      return `background: #fff7ed; color: #9a3412; border: 1px solid #ffedd5;`;
    return `background: #fee2e2; color: #991b1b; border: 1px solid #fecaca;`;
  }}
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 42px;
  padding: 0 16px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 600;
  transition: transform 0.05s ease, background 0.2s ease;

  &:hover { background: #f9fafb; }
  &:active { transform: translateY(1px); }

  ${({ variant }) =>
    variant === "danger" &&
    `
      color: #b91c1c;
      border-color: #fecaca;
      background: #fff1f2;
      &:hover { background: #ffe4e6; }
    `}

  ${({ disabled }) =>
    disabled &&
    `
      cursor: not-allowed;
      opacity: .6;
    `}
`;

const Note = styled.p`
  margin: 12px 0 0 0;
  color: #6b7280;
  font-size: 0.95rem;
`;

/* ===== helpers ===== */
const getEmailFromStorageOrToken = () => {
  try {
    const stored = JSON.parse(localStorage.getItem("user") || "null");
    const email = stored?.Email || stored?.userEmail;
    if (email) return email;

    const token = localStorage.getItem("token");
    if (token) {
      const dec = jwtDecode(token);
      return dec?.Email || dec?.email || dec?.userEmail || "";
    }
  } catch {
    // ignore
  }
  return "";
};

const formatPLDate = (dt) => {
  if (!dt) return "-";
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("pl-PL", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

const isActiveLicense = (licenseType, licenseExpire) => {
  if (!licenseType) return false;

  const type = String(licenseType).trim().toUpperCase();

  // Any DEMO is treated as demo (not "active paid"), but still time-bound
  if (type.startsWith("DEMO")) {
    if (!licenseExpire) return false;
    const d = new Date(licenseExpire);
    return !Number.isNaN(d.getTime()) && d.getTime() > Date.now();
  }

  // Paid licenses:
  if (typeof licenseExpire === "string" && licenseExpire.toUpperCase() === "NO EXPIRE")
    return true;
  const d = licenseExpire ? new Date(licenseExpire) : null;
  return !!(d && !Number.isNaN(d.getTime()) && d.getTime() > Date.now());
};

const isDemo = (licenseType) => {
  if (!licenseType) return false;
  return String(licenseType).trim().toUpperCase().startsWith("DEMO");
};

/* ===== main component ===== */
const Subscription = () => {
  const [email] = useState(getEmailFromStorageOrToken());
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);

  const [loadingUser, setLoadingUser] = useState(true);
  const [loadingCompany, setLoadingCompany] = useState(true);

  const [errorUser, setErrorUser] = useState("");
  const [errorCompany, setErrorCompany] = useState("");

  // Stripe subscription info
  const [subscription, setSubscription] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const [errorSubscription, setErrorSubscription] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);

  const active = useMemo(
    () => isActiveLicense(user?.licenseType, user?.licenseExpire),
    [user]
  );
  const demo = useMemo(() => isDemo(user?.licenseType), [user]);

  // Derived flags for Stripe status
  const hasStripeSub = !!subscription?.subscriptionId;
  const isCancelScheduled = !!subscription?.cancelAtPeriodEnd;
  const isStripeCanceled = subscription?.stripeStatus === "canceled";

  // Fetch user + company data
  useEffect(() => {
    let mounted = true;

    const fetchUser = async () => {
      setLoadingUser(true);
      setErrorUser("");
      try {
        const tryOne = await axios.get(`${API_BASE}/api/users`, {
          params: email ? { email } : undefined,
        });
        const payload = tryOne.data;

        if (Array.isArray(payload)) {
          const found =
            (email &&
              payload.find(
                (u) =>
                  String(u?.userEmail || "").toLowerCase() ===
                  String(email).toLowerCase()
              )) ||
            null;
          if (mounted) setUser(found);
        } else {
          if (mounted) setUser(payload);
        }
      } catch (err) {
        if (mounted)
          setErrorUser("Nie udało się pobrać danych użytkownika.");
      } finally {
        if (mounted) setLoadingUser(false);
      }
    };

    const fetchCompany = async () => {
      if (!email) {
        setLoadingCompany(false);
        return;
      }
      setLoadingCompany(true);
      setErrorCompany("");
      try {
        const res = await axios.get(
          `${API_BASE}/api/invoice/${encodeURIComponent(email)}`
        );
        if (mounted) setCompany(res.data || null);
      } catch (err) {
        if (mounted)
          setErrorCompany("Nie udało się pobrać danych firmy.");
      } finally {
        if (mounted) setLoadingCompany(false);
      }
    };

    fetchUser();
    fetchCompany();

    return () => {
      mounted = false;
    };
  }, [email]);

  // Fetch Stripe subscription data
  useEffect(() => {
    if (!email) {
      setSubscription(null);
      setLoadingSubscription(false);
      return;
    }

    let mounted = true;

    const fetchSubscription = async () => {
      setLoadingSubscription(true);
      setErrorSubscription("");
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API_BASE}/api/my-subscription`, {
          headers: token ? { Authorization: token } : {},
        });
        if (mounted) {
          setSubscription(res.data); // can be object or null
        }
      } catch (err) {
        console.error("❌ Error fetching subscription:", err);
        if (mounted) {
          setErrorSubscription(
            "Nie udało się pobrać danych subskrypcji Stripe."
          );
        }
      } finally {
        if (mounted) setLoadingSubscription(false);
      }
    };

    fetchSubscription();
    return () => {
      mounted = false;
    };
  }, [email]);

  /* ===== Button handlers ===== */

  // Cancel Stripe subscription (cancel at period end)
  const handleCancelStripe = async () => {
    if (!subscription || !subscription.subscriptionId) {
      alert("Brak aktywnej subskrypcji Stripe do anulowania.");
      return;
    }

    // extra safety: do nothing if already scheduled/canceled
    if (subscription.cancelAtPeriodEnd || subscription.stripeStatus === "canceled") {
      return;
    }

    if (
      !window.confirm(
        "Czy na pewno chcesz anulować automatyczne odnawianie subskrypcji? " +
          "Subskrypcja zostanie zakończona z końcem bieżącego okresu rozliczeniowego."
      )
    ) {
      return;
    }

    try {
      setCancelLoading(true);
      const token = localStorage.getItem("token");

      const res = await axios.post(
        `${API_BASE}/api/my-subscription/cancel`,
        { cancelAtPeriodEnd: true }, // schedule cancellation at period end
        { headers: token ? { Authorization: token } : {} }
      );

      const apiSub = res.data?.subscription || {};

      // Update local subscription state
      setSubscription((prev) =>
        prev
          ? {
              ...prev,
              stripeStatus: apiSub.status ?? prev.stripeStatus,
              cancelAtPeriodEnd:
                apiSub.cancel_at_period_end ?? prev.cancelAtPeriodEnd,
              cancelAt:
                apiSub.cancel_at != null
                  ? new Date(apiSub.cancel_at * 1000).toISOString()
                  : prev.cancelAt,
            }
          : prev
      );

      alert(
        "Subskrypcja została ustawiona do anulowania z końcem bieżącego okresu rozliczeniowego. " +
          "Wyślemy do Ciebie mailowe potwierdzenie."
      );
    } catch (err) {
      console.error("❌ Error cancelling subscription:", err);
      alert(
        "Nie udało się anulować subskrypcji. Spróbuj ponownie lub skontaktuj się z nami."
      );
    } finally {
      setCancelLoading(false);
    }
  };

  const handleDeleteCompanyData = () => {
    console.log("TODO: delete company/invoice data for:", {
      email,
      company,
    });
    // later: DELETE /api/invoice/:email
  };

  /* ===== Render ===== */
  return (
    <DashboardWrapper>
      <Sidebar />

      <Content>
        <TopBar />
        <PageBody>
          <Title>
            <FaIdCard />
            Ustawienia i Dane / Subskrypcja
          </Title>

          <Grid>
            {/* LEFT: Subscription details */}
            <Card>
              <SectionHeader>Twoja subskrypcja</SectionHeader>

              {loadingUser ? (
                <div>Ładowanie danych subskrypcji…</div>
              ) : errorUser ? (
                <div style={{ color: "#b91c1c" }}>{errorUser}</div>
              ) : !user ? (
                <div>
                  Nie znaleziono danych użytkownika dla:{" "}
                  <b>{email || "-"}</b>
                </div>
              ) : (
                <>
                  <Row>
                    <Key>Email</Key>
                    <Val>{user.userEmail || email || "-"}</Val>
                  </Row>

                  <Row>
                    <Key>Typ licencji</Key>
                    <Val>
                      {user.licenseType || "-"}{" "}
                      {active ? (
                        demo ? (
                          <Badge type="demo">DEMO</Badge>
                        ) : (
                          <Badge type="active">AKTYWNA</Badge>
                        )
                      ) : (
                        <Badge type="inactive">NIEAKTYWNA</Badge>
                      )}
                    </Val>
                  </Row>

                  <Row>
                    <Key>Ważność</Key>
                    <Val>
                      {typeof user.licenseExpire === "string" &&
                      user.licenseExpire.toUpperCase() === "NO EXPIRE"
                        ? "Bezterminowa"
                        : formatPLDate(user.licenseExpire)}
                    </Val>
                  </Row>

                  <Row>
                    <Key>Status</Key>
                    <Val>
                      {active
                        ? demo
                          ? "Demo aktywne — możesz wykupić subskrypcję."
                          : "Subskrypcja/ licencja aktywna."
                        : "Brak aktywnej subskrypcji."}
                    </Val>
                  </Row>

                  {/* Stripe subscription info */}
                  <Row>
                    <Key>Stripe Subscription ID</Key>
                    <Val>
                      {loadingSubscription ? (
                        "Ładowanie…"
                      ) : errorSubscription ? (
                        <span style={{ color: "#b91c1c" }}>
                          {errorSubscription}
                        </span>
                      ) : subscription?.subscriptionId ? (
                        subscription.subscriptionId
                      ) : (
                        "-"
                      )}
                    </Val>
                  </Row>

                  {subscription && (
                    <Row>
                      <Key>Status w Stripe</Key>
                      <Val>
                        {subscription.stripeStatus || "brak danych"}
                        {subscription.cancelAtPeriodEnd &&
                          subscription.cancelAt && (
                            <span
                              style={{
                                display: "inline-block",
                                marginLeft: 6,
                                color: "#92400e",
                                fontSize: "0.9rem",
                              }}
                            >
                              (anulowanie z końcem okresu:{" "}
                              {formatPLDate(subscription.cancelAt)})
                            </span>
                          )}
                      </Val>
                    </Row>
                  )}

                  <Actions>
                    <Button
                      variant="danger"
                      onClick={handleCancelStripe}
                      disabled={
                        !hasStripeSub ||
                        cancelLoading ||
                        !active ||
                        demo ||
                        isCancelScheduled ||
                        isStripeCanceled
                      }
                      title={
                        !hasStripeSub
                          ? "Brak subskrypcji Stripe powiązanej z tym kontem"
                          : isStripeCanceled
                          ? "Subskrypcja została już anulowana w Stripe"
                          : isCancelScheduled
                          ? "Anulowanie subskrypcji jest już zaplanowane z końcem obecnego okresu"
                          : !active || demo
                          ? "Brak aktywnej płatnej subskrypcji do anulowania"
                          : ""
                      }
                    >
                      <FaTimesCircle />
                      {cancelLoading
                        ? "Anulowanie..."
                        : isStripeCanceled
                        ? "Subskrypcja anulowana"
                        : isCancelScheduled
                        ? "Anulowanie zaplanowane"
                        : "Anuluj subskrypcję (Stripe)"}
                    </Button>
                  </Actions>

                  <Note>
                    Jeśli chcesz anulować lub zmienić swoją subskrypcję,
                    możesz też napisać do nas:{" "}
                    <a href="mailto:biuro@analizyiraporty.pl">
                      biuro@analizyiraporty.pl
                    </a>
                  </Note>
                </>
              )}
            </Card>

            {/* RIGHT: Company / Invoice data */}
            <Card>
              <SectionHeader>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <FaBuilding /> Dane firmy (do faktury)
                </span>
              </SectionHeader>

              {loadingCompany ? (
                <div>Ładowanie danych firmy…</div>
              ) : errorCompany ? (
                <div style={{ color: "#b91c1c" }}>{errorCompany}</div>
              ) : !company ? (
                <div>
                  Brak zapisanych danych firmy dla:{" "}
                  <b>{email || "-"}</b>
                </div>
              ) : (
                <>
                  <Row>
                    <Key>Nazwa</Key>
                    <Val>{company.nazwa || "-"}</Val>
                  </Row>
                  <Row>
                    <Key>NIP</Key>
                    <Val>{company.nip || "-"}</Val>
                  </Row>
                  <Row>
                    <Key>Województwo</Key>
                    <Val>{company.wojewodztwo || "-"}</Val>
                  </Row>
                  <Row>
                    <Key>Powiat</Key>
                    <Val>{company.powiat || "-"}</Val>
                  </Row>
                  <Row>
                    <Key>Gmina</Key>
                    <Val>{company.gmina || "-"}</Val>
                  </Row>
                  <Row>
                    <Key>Miejscowość</Key>
                    <Val>{company.miejscowosc || "-"}</Val>
                  </Row>
                  <Row>
                    <Key>Kod pocztowy</Key>
                    <Val>{company.kodPocztowy || "-"}</Val>
                  </Row>
                  <Row>
                    <Key>Ulica</Key>
                    <Val>{company.ulica || "-"}</Val>
                  </Row>
                  <Row>
                    <Key>Nr nieruchomości</Key>
                    <Val>{company.nrNieruchomosci || "-"}</Val>
                  </Row>
                  <Row>
                    <Key>Nr lokalu</Key>
                    <Val>{company.nrLokalu || "-"}</Val>
                  </Row>

                  <Actions>
                    <Button variant="danger" onClick={handleDeleteCompanyData}>
                      <FaTrashAlt />
                      Usuń dane firmy
                    </Button>
                  </Actions>
                </>
              )}
            </Card>
          </Grid>
        </PageBody>
      </Content>
    </DashboardWrapper>
  );
};

export default Subscription;
